import { PropsWithChildren, createContext, useEffect, useRef } from 'react';
import { ClipStore, createStore } from './store';
import { useQuery } from '@tanstack/react-query';
import { fetchClip, incrementViews } from './service';
import { extractError } from '../../../../utils/api';

type ClipContext = {
  store: ClipStore;
};

export const ClipContext = createContext<ClipContext>({
  store: createStore(),
});

interface Props {
  id: string;
  secret: string;
}

export const ClipProvider: React.FC<PropsWithChildren<Props>> = ({ id, secret, children }) => {
  const store = useRef(createStore()).current;
  const state = store();

  const { data, isFetching, error } = useQuery({
    queryKey: ['clip', id, secret],
    queryFn: () => fetchClip(id, secret),
    onSuccess: async () => {
      const viewCount = await incrementViews(id);
      state.incrementView(viewCount);
    },
    onError: error => {
      state.fetchFailed(extractError(error));
    },
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (error instanceof Error) {
      state.fetchFailed(error.message);
    }
  }, [error]);

  useEffect(() => {
    if (isFetching) state.fetchStarted();
    else state.fetchEnded();
  }, [isFetching]);

  useEffect(() => {
    if (data) {
      state.clipLoaded(data);
    }
  }, [data]);

  return <ClipContext.Provider value={{ store }}>{children}</ClipContext.Provider>;
};
